import { Box, Button, Container, Typography } from '@mui/material'
import {
  DataGrid,
  GridCellParams,
  GridColDef,
  GridComparatorFn,
} from '@mui/x-data-grid'
import { useEffect, useState } from 'react'
import { Dispatch, SetStateAction } from 'react'

import {
  AnnouncementListItem,
  AnnouncementListResponseData,
} from '../../../../providers/Announcement/types'
import { themeColors } from '../../../../theme'
import { dateFormatters } from '../../../../utilities/HelperFunctions'
import { ViewAnnouncementModal } from './ViewAnnouncementsModal'

interface AnnouncementViewProps {
  deliveredAnnouncements: AnnouncementListResponseData
  selectedAnnouncement: AnnouncementListItem
  showViewAnnouncementModal: boolean
  setShowViewAnnouncementModal: Dispatch<SetStateAction<boolean>>
  handleEditProviderModal: (selectedAnnouncementId: number) => void
  getListOfAnnouncements: () => void
  cancelScheduledAnnouncement: (announcementId: number) => Promise<void>
}

const dayInMonthComparator: GridComparatorFn<Date> = (v1, v2) =>
  new Date(v1) < new Date(v2) ? -1 : 1

const COLUMNS: GridColDef[] = [
  {
    field: 'dateSent',
    headerName: 'Sent On',
    minWidth: 180,
    type: 'date',
    sortComparator: dayInMonthComparator,
  },
  { field: 'scheduledFor', headerName: 'Scheduled For', minWidth: 170 },
  { field: 'canceled', headerName: 'Cancel Schedule', minWidth: 150 },
  { field: 'name', headerName: 'Announcement', minWidth: 300 },
  { field: 'sentBy', headerName: 'Sent By', minWidth: 120 },
  { field: ' ', headerName: 'View/Edit', minWidth: 110 },
].map((col) => ({ ...col, disableColumnMenu: true }))

export const AnnouncementView = ({
  deliveredAnnouncements,
  selectedAnnouncement,
  showViewAnnouncementModal,
  setShowViewAnnouncementModal,
  handleEditProviderModal,
  getListOfAnnouncements,
  cancelScheduledAnnouncement,
}: AnnouncementViewProps) => {
  const [formattedAnnouncements, setFormattedAnnouncements] = useState<
    AnnouncementListResponseData | []
  >([])

  useEffect(() => {
    const activeAnnouncements = deliveredAnnouncements.filter(
      (a) => !a.canceled
    )
    setFormattedAnnouncements(
      activeAnnouncements.map((deliveredAnnouncement) => ({
        ...deliveredAnnouncement,
        dateSent:
          deliveredAnnouncement.dateSent === 'Pending'
            ? deliveredAnnouncement.dateSent
            : dateFormatters.prettyDate(deliveredAnnouncement.dateSent),
        scheduledFor: deliveredAnnouncement.scheduledFor
          ? dateFormatters.prettyDateWithTime(
              deliveredAnnouncement.scheduledFor
            )
          : 'N/A',
      }))
    )
  }, [deliveredAnnouncements])

  const [dataColumns, setDataColumns] = useState<GridColDef[]>(COLUMNS)

  useEffect(() => {
    if (formattedAnnouncements) {
      const dataColumns = COLUMNS.map((col) => {
        if (col.field === 'name') {
          return {
            ...col,
            renderCell: (params: GridCellParams) => (
              <Box sx={{ minWidth: '100%' }}>
                <Typography
                  sx={{
                    color: themeColors.blue40,
                    fontSize: '14px',
                    maxWidth: '100%',
                    wordBreak: 'break-word',
                    whiteSpace: 'normal',
                    textAlign: 'left',
                    minWidth: '100%',
                  }}
                >
                  {params.value}
                </Typography>
              </Box>
            ),
          }
        }
        if (col.field === 'canceled') {
          return {
            ...col,
            renderCell: (params: GridCellParams) => {
              // Find the row data to check if dateSent is 'Pending'
              const rowData = formattedAnnouncements.find(
                (announcement: any) => announcement.id === params.id
              )
              const isPending = rowData?.dateSent === 'Pending'

              if (isPending) {
                return (
                  <Box>
                    <Button
                      id={`edit-role-btn-${Number(params.id)}`}
                      sx={{
                        minWidth: '100%',
                      }}
                      variant='contained'
                      color='warning'
                      onClick={() => {
                        cancelScheduledAnnouncement(Number(rowData.id))
                        getListOfAnnouncements()
                      }}
                    >
                      Cancel Delivery
                    </Button>
                  </Box>
                )
              } else {
                return (
                  <Box sx={{ minWidth: '100%' }}>
                    <Typography
                      sx={{
                        color: themeColors.blue40,
                        fontSize: '14px',
                        maxWidth: '100%',
                        wordBreak: 'break-word',
                        whiteSpace: 'normal',
                        textAlign: 'left',
                        minWidth: '100%',
                      }}
                    >
                      {params.value}
                    </Typography>
                  </Box>
                )
              }
            },
          }
        }
        if (col.headerName === 'View/Edit') {
          return {
            field: ' ',
            headerName: 'View/Edit',
            width: 120,
            renderCell: (params: GridCellParams) => (
              <Box>
                <Button
                  id={`edit-role-btn-${Number(params.id)}`}
                  sx={{
                    minWidth: '100%',
                  }}
                  variant='contained'
                  color='warning'
                  onClick={() => {
                    handleEditProviderModal(Number(params.id))
                  }}
                >
                  View/Edit
                </Button>
              </Box>
            ),
          }
        }
        return col
      })

      setDataColumns(dataColumns)
    }
  }, [formattedAnnouncements])

  return (
    <Container
      data-testid='announcement'
      sx={{ height: '100%', width: '100%' }}
    >
      <ViewAnnouncementModal
        selectedAnnouncement={selectedAnnouncement}
        showViewAnnouncementModal={showViewAnnouncementModal}
        setShowViewAnnouncementModal={setShowViewAnnouncementModal}
      />

      <DataGrid
        getRowId={(row: any) => row.id}
        rows={formattedAnnouncements}
        columns={dataColumns}
        disableColumnFilter={true}
        isRowSelectable={() => false}
        sx={{
          fontFamily: 'Lato',
          fontStyle: 'normal',
          fontWeight: 400,
          minHeight: '200px',
          fontSize: '14px',
          lineHeight: '100%',
          verticalAlign: 'center',
          border: 'none',
          '& .MuiDataGrid-cell:active, .MuiDataGrid-cell:focus, .MuiDataGrid-cell:focus-within, .MuiDataGrid-cell:hover':
            {
              border: 'none',
              outline: 'none',
            },
          '& .MuiTableCell-root': {
            border: 'none',
            outline: 'none',
          },
          '& .MuiDataGrid-cell:hover': {
            pointer: 'cursor',
          },
          '& .MuiDataGrid-columnHeaderTitleContainer, .MuiDataGrid-columnHeaderTitleContainer:focus, .MuiDataGrid-columnHeaderTitleContainer:focus-within':
            {
              fontWeight: 700,
              fontSize: '14px',
              lineHeight: '100%',
              textTransform: 'uppercase',
              border: 'none',
              outline: 'none',
            },
        }}
      />
    </Container>
  )
}
